import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  ACTIVE_TIMER_STORAGE_KEY,
  AD_PERSONALIZATION_CONSENT_STORAGE_KEY,
  APP_SETTINGS_STORAGE_KEY,
  DATA_DISCLOSURE_CONSENT_STORAGE_KEY,
  FRICTION_START_STORAGE_KEY,
  LEGAL_CONSENT_DONE_STORAGE_KEY,
  ONBOARDING_DONE_STORAGE_KEY,
  PARKING_DRAFT_STORAGE_KEY,
  STUCK_TIMER_HELPER_COUNT_STORAGE_KEY,
  TINY_WIN_DRAFT_STORAGE_KEY,
  UX_HINT_LEVEL_STORAGE_KEY
} from '../constants';
import { readAdConsentState } from '../lib/adConsent';
import { nowMs } from '../lib/time';
import type { ActiveTimer, AppFont, AppLanguage, AppSettings, AppTheme, AppVisualStyle } from '../types';

type SetState<T> = Dispatch<SetStateAction<T>>;

type UseAppClientBootstrapParams = {
  defaultSettings: AppSettings;
  appThemeSet: Set<AppTheme>;
  appVisualStyleSet: Set<AppVisualStyle>;
  appFontSet: Set<AppFont>;
  appLanguageSet: Set<AppLanguage>;
  setActiveTimer: SetState<ActiveTimer | null>;
  setFrictionStartMs: SetState<number | null>;
  setTinyWinDraft: SetState<string>;
  setParkingDraft: SetState<string>;
  setHintLevel: SetState<number>;
  setStuckTimerHelperCount: SetState<number>;
  setAdConsentStatus: SetState<'unknown' | 'accepted' | 'rejected' | 'not_required'>;
  setShowLegalConsent: SetState<boolean>;
  setShowDataDisclosure: SetState<boolean>;
  setShowOnboarding: SetState<boolean>;
  setSettings: SetState<AppSettings>;
};

type UseAppClientPersistenceParams = {
  activeTimer: ActiveTimer | null;
  frictionStartMs: number | null;
  tinyWinDraft: string;
  parkingDraft: string;
  settings: AppSettings;
  adConsentStatus: 'unknown' | 'accepted' | 'rejected' | 'not_required';
};

export function useAppClientBootstrap({
  defaultSettings,
  appThemeSet,
  appVisualStyleSet,
  appFontSet,
  appLanguageSet,
  setActiveTimer,
  setFrictionStartMs,
  setTinyWinDraft,
  setParkingDraft,
  setHintLevel,
  setStuckTimerHelperCount,
  setAdConsentStatus,
  setShowLegalConsent,
  setShowDataDisclosure,
  setShowOnboarding,
  setSettings
}: UseAppClientBootstrapParams): void {
  useEffect(() => {
    const raw = localStorage.getItem(ACTIVE_TIMER_STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as ActiveTimer;
      setActiveTimer({
        ...parsed,
        notificationId: Number.isFinite(parsed.notificationId as number) ? (parsed.notificationId as number) : null
      });
    } catch {
      localStorage.removeItem(ACTIVE_TIMER_STORAGE_KEY);
    }
  }, [setActiveTimer]);

  useEffect(() => {
    const raw = localStorage.getItem(FRICTION_START_STORAGE_KEY);
    if (!raw) return;
    const value = Number(raw);
    if (Number.isFinite(value) && value > 0) setFrictionStartMs(value);
  }, [setFrictionStartMs]);

  useEffect(() => {
    const tinyWinRaw = localStorage.getItem(TINY_WIN_DRAFT_STORAGE_KEY);
    if (tinyWinRaw) setTinyWinDraft(tinyWinRaw);
    const parkingRaw = localStorage.getItem(PARKING_DRAFT_STORAGE_KEY);
    if (parkingRaw) setParkingDraft(parkingRaw);

    const hintRaw = localStorage.getItem(UX_HINT_LEVEL_STORAGE_KEY);
    const hint = Number(hintRaw ?? '0');
    if (Number.isFinite(hint) && hint >= 0) setHintLevel(hint);

    const stuckHelperRaw = localStorage.getItem(STUCK_TIMER_HELPER_COUNT_STORAGE_KEY);
    const stuckHelper = Number(stuckHelperRaw ?? '0');
    if (Number.isFinite(stuckHelper) && stuckHelper >= 0) setStuckTimerHelperCount(stuckHelper);

    const onboardingDone = localStorage.getItem(ONBOARDING_DONE_STORAGE_KEY) === '1';
    const legalDone = localStorage.getItem(LEGAL_CONSENT_DONE_STORAGE_KEY) === '1';
    const dataDisclosureDone = localStorage.getItem(DATA_DISCLOSURE_CONSENT_STORAGE_KEY) !== null;
    const adConsent = readAdConsentState(localStorage.getItem(AD_PERSONALIZATION_CONSENT_STORAGE_KEY), nowMs());
    setAdConsentStatus(adConsent.status);

    if (!legalDone) {
      setShowLegalConsent(true);
    } else if (!dataDisclosureDone) {
      setShowDataDisclosure(true);
    } else if (!onboardingDone) {
      setShowOnboarding(true);
    }

    const settingsRaw = localStorage.getItem(APP_SETTINGS_STORAGE_KEY);
    if (!settingsRaw) return;
    try {
      const parsed = JSON.parse(settingsRaw) as Partial<AppSettings>;
      setSettings((prev) => {
        const merged = { ...prev, ...parsed };
        if (!appThemeSet.has(merged.theme)) merged.theme = defaultSettings.theme;
        if (!appVisualStyleSet.has(merged.visualStyle)) merged.visualStyle = defaultSettings.visualStyle;
        if (!appFontSet.has(merged.font)) merged.font = defaultSettings.font;
        if (!appLanguageSet.has(merged.language)) merged.language = defaultSettings.language;
        if (!['off', 'quiet', 'standard'].includes(merged.notificationStyle)) {
          merged.notificationStyle = defaultSettings.notificationStyle;
        }
        return merged;
      });
    } catch {
      // ignore invalid settings
    }
  }, [
    appFontSet,
    appLanguageSet,
    appVisualStyleSet,
    defaultSettings.notificationStyle,
    appThemeSet,
    defaultSettings.font,
    defaultSettings.language,
    defaultSettings.theme,
    defaultSettings.visualStyle,
    setAdConsentStatus,
    setHintLevel,
    setParkingDraft,
    setSettings,
    setShowDataDisclosure,
    setShowLegalConsent,
    setShowOnboarding,
    setStuckTimerHelperCount,
    setTinyWinDraft
  ]);
}

export function useAppClientPersistence({
  activeTimer,
  frictionStartMs,
  tinyWinDraft,
  parkingDraft,
  settings,
  adConsentStatus
}: UseAppClientPersistenceParams): void {
  useEffect(() => {
    if (activeTimer) {
      localStorage.setItem(ACTIVE_TIMER_STORAGE_KEY, JSON.stringify(activeTimer));
    } else {
      localStorage.removeItem(ACTIVE_TIMER_STORAGE_KEY);
    }
  }, [activeTimer]);

  useEffect(() => {
    if (frictionStartMs === null) {
      localStorage.removeItem(FRICTION_START_STORAGE_KEY);
      return;
    }
    localStorage.setItem(FRICTION_START_STORAGE_KEY, String(frictionStartMs));
  }, [frictionStartMs]);

  useEffect(() => {
    if (tinyWinDraft.trim().length === 0) {
      localStorage.removeItem(TINY_WIN_DRAFT_STORAGE_KEY);
      return;
    }
    localStorage.setItem(TINY_WIN_DRAFT_STORAGE_KEY, tinyWinDraft);
  }, [tinyWinDraft]);

  useEffect(() => {
    if (parkingDraft.trim().length === 0) {
      localStorage.removeItem(PARKING_DRAFT_STORAGE_KEY);
      return;
    }
    localStorage.setItem(PARKING_DRAFT_STORAGE_KEY, parkingDraft);
  }, [parkingDraft]);

  useEffect(() => {
    localStorage.setItem(APP_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    localStorage.setItem(
      AD_PERSONALIZATION_CONSENT_STORAGE_KEY,
      JSON.stringify({
        version: 1,
        status: adConsentStatus,
        updatedAtMs: nowMs()
      })
    );
  }, [adConsentStatus]);
}
